import React, { useMemo, useState } from 'react';
import { Plus, Trash2, Copy, X } from 'lucide-react';
import Overlay from './PlannerOverlay';
import { ROUTINE_PRESETS, AUDIENCE_CHIPS, ARRIVAL_PURPOSES, FALLBACK_CATEGORY, categoryMap } from './plannerCategories';
import { fmtDayShort, toMin, fromMin } from './plannerShared';
import SlotsEditor from './SlotsEditor';

// Side panel to create or edit one planner block. All validation that matters happens on the
// server (services/planner/blocks.js); this only shapes the request.

function initialForm(b, cats) {
  const category = b.category || (cats.training_pitch ? 'training_pitch' : Object.keys(cats)[0]);
  return {
    date: b.date,
    category,
    title: b.title ?? (cats[category] || FALLBACK_CATEGORY).default_title,
    timing: b.timing || 'clock',
    start_time: b.start_time || b.resolved_start || '10:00',
    end_time: b.end_time || b.resolved_end || '11:00',
    ko_offset_min: b.ko_offset_min ?? -60,
    duration_min: b.duration_min ?? 60,
    location: b.location || '',
    audience: b.audience || ['all'],
    specific_points: b.specific_points || '',
    staff_note: b.staff_note || '',
    topics: b.topics || [],
    routine: b.routine || ROUTINE_PRESETS[0].routine,
    arrival_purpose: b.arrival_purpose || 'everybody',
    rotation: b.rotation || null,
    slot_minutes: b.slot_minutes ?? '',
  };
}

// A new rotation starts as the trainer's usual one: individual meetings / gym, swapping halfway.
function defaultRotation(form, cats) {
  const s = toMin(form.start_time);
  const e = toMin(form.end_time);
  const half = s + Math.round((e - s) / 2 / 5) * 5;
  const station = (key, fallback) => ({ category: cats[key] && !cats[key].archived_at ? key : fallback, title: cats[key]?.default_title || 'Station', location: '' });
  return { swap_at: fromMin(half), station_a: station('individual_meeting', 'other'), station_b: station('gym', 'other') };
}

const field = { display: 'flex', flexDirection: 'column', gap: 4 };
const sectionTitle = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' };

export default function BlockPanel({ block, week, canEdit, busy, onClose, onSave, onDelete, onDuplicate, onSaveSlots, onAutofillSlots }) {
  const isNew = !block.id;
  const cats = useMemo(() => categoryMap(week.categories), [week.categories]);
  const catOf = key => cats[key] || FALLBACK_CATEGORY;
  // Hidden categories aren't offered, except the one this block already has.
  const categoryOptions = week.categories.filter(c => !c.archived_at || c.key === block.category);
  const [form, setForm] = useState(() => initialForm(block, cats));
  const [playerFilter, setPlayerFilter] = useState('');
  const [dupDates, setDupDates] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const day = week.days.find(d => d.date === form.date);
  const fixture = day?.fixtures?.find(f => f.counts_for_labels) || day?.fixtures?.[0] || null;
  const canAnchor = !!fixture;

  const setCategory = category => setForm(f => {
    const defaults = [catOf(f.category).default_title, ...ARRIVAL_PURPOSES.map(p => p.title)];
    const title = !f.title || defaults.includes(f.title) ? catOf(category).default_title : f.title;
    const next = { ...f, category, title };
    // An arrival is a moment, not a session: a new arrival block defaults to 15 minutes.
    if (category === 'arrival' && isNew && f.timing === 'clock') next.end_time = fromMin(toMin(f.start_time) + 15);
    if (category === 'arrival') next.arrival_purpose = 'everybody';
    if (category === 'rotation') {
      if (f.timing !== 'clock') next.timing = 'clock';
      if (isNew && toMin(f.end_time) - toMin(f.start_time) < 60) next.end_time = fromMin(toMin(f.start_time) + 120);
      if (!f.rotation) next.rotation = defaultRotation(next, cats);
    }
    return next;
  });

  const setStation = (k, patch) => set('rotation', { ...form.rotation, [k]: { ...form.rotation[k], ...patch } });
  const stationOptions = week.categories.filter(c => c.key !== 'rotation' && (!c.archived_at || [form.rotation?.station_a?.category, form.rotation?.station_b?.category].includes(c.key)));
  const meetingCapable = form.category === 'individual_meeting'
    || (form.category === 'rotation' && form.rotation && [form.rotation.station_a, form.rotation.station_b].some(st => st.category === 'individual_meeting'));
  const hasGym = form.category === 'gym' || (form.category === 'rotation' && form.rotation && [form.rotation.station_a, form.rotation.station_b].some(st => st.category === 'gym'));
  // The slot windows shown come from the saved block; unsaved time / length / station changes make them stale.
  const slotsStale = !isNew && (String(form.slot_minutes || '') !== String(block.slot_minutes || '') || form.start_time !== block.start_time
    || form.end_time !== block.end_time || JSON.stringify(form.rotation) !== JSON.stringify(block.rotation || null) || form.date !== block.date);

  const setArrivalPurpose = purpose => setForm(f => {
    const title = !f.title || ARRIVAL_PURPOSES.some(p => p.title === f.title) ? ARRIVAL_PURPOSES.find(p => p.id === purpose).title : f.title;
    return { ...f, arrival_purpose: purpose, title };
  });

  const applyPreset = p => setForm(f => ({ ...f, title: p.label, start_time: p.start_time, end_time: p.end_time, routine: p.routine, timing: 'clock' }));

  // Audience: 'Staff only' excludes everything else; picking anything specific drops 'Everyone'.
  const toggleToken = t => setForm(f => {
    let a = f.audience.includes(t) ? f.audience.filter(x => x !== t) : [...f.audience, t];
    if (t === 'staff' && a.includes('staff')) a = ['staff'];
    else if (t === 'all' && a.includes('all')) a = ['all'];
    else a = a.filter(x => x !== 'staff' && (t === 'all' || x !== 'all'));
    return { ...f, audience: a.length ? a : ['all'] };
  });
  const playerIds = form.audience.filter(t => t.startsWith('player:')).map(t => Number(t.slice(7)));
  const togglePlayer = id => setForm(f => {
    const tok = `player:${id}`;
    const a = f.audience.includes(tok) ? f.audience.filter(x => x !== tok) : [...f.audience.filter(x => x !== 'all' && x !== 'staff'), tok];
    return { ...f, audience: a.length ? a : ['all'] };
  });
  const players = useMemo(() => {
    const q = playerFilter.trim().toLowerCase();
    return week.players.filter(p => !q || p.name.toLowerCase().includes(q) || String(p.squad_number || '').includes(q));
  }, [week.players, playerFilter]);

  const koAbs = Math.abs(form.ko_offset_min);
  const koDir = form.ko_offset_min < 0 ? 'before' : 'after';
  const setKo = (abs, dir) => set('ko_offset_min', (dir === 'before' ? -1 : 1) * Math.max(0, Math.round(Number(abs) || 0)));
  const koPreview = fixture?.kickoff_time
    ? `${fromMin(toMin(fixture.kickoff_time) + form.ko_offset_min)} – ${fromMin(toMin(fixture.kickoff_time) + form.ko_offset_min + Number(form.duration_min || 0))}`
    : null;

  const setTopic = (i, patch) => set('topics', form.topics.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const setRoutine = patch => set('routine', { ...form.routine, ...patch });
  const setMeal = patch => set('routine', { ...form.routine, meal: { ...(form.routine?.meal || { label: 'Breakfast', start: '09:00', end: '10:00' }), ...patch } });

  const submit = () => onSave({
    date: form.date,
    category: form.category,
    title: form.title.trim(),
    location: form.location.trim() || null,
    timing: form.timing,
    start_time: form.timing === 'clock' ? form.start_time : null,
    end_time: form.timing === 'clock' ? form.end_time : null,
    ko_offset_min: form.timing === 'kickoff' ? form.ko_offset_min : null,
    duration_min: form.timing === 'kickoff' ? Number(form.duration_min) : null,
    audience: form.audience,
    specific_points: form.specific_points.trim() || null,
    staff_note: form.staff_note.trim() || null,
    topics: form.category === 'meeting_video' ? form.topics.filter(t => t.text.trim()) : [],
    routine: form.category === 'routine' ? form.routine : null,
    arrival_purpose: form.category === 'arrival' ? form.arrival_purpose : null,
    rotation: form.category === 'rotation' ? form.rotation : null,
    slot_minutes: meetingCapable && form.slot_minutes !== '' ? Number(form.slot_minutes) : null,
  });

  const otherDays = week.days.filter(d => d.date !== block.date);

  return (
    <Overlay title={isNew ? 'New block' : canEdit ? 'Edit block' : form.title} onClose={onClose} width={460}>
      <fieldset disabled={!canEdit || busy} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        <label className="pl-label">Category
          <select className="pl-input" value={form.category} onChange={e => setCategory(e.target.value)}>
            {categoryOptions.map(c => <option key={c.key} value={c.key}>{c.label}{c.archived_at ? ' (hidden)' : ''}</option>)}
          </select>
        </label>

        {form.category === 'routine' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ROUTINE_PRESETS.map(p => (
              <button key={p.id} type="button" className="pl-btn" onClick={() => applyPreset(p)}>{p.label} ({p.start_time}–{p.end_time})</button>
            ))}
          </div>
        )}

        {form.category === 'arrival' && (
          <div style={field}>
            <span style={sectionTitle}>Arrival for</span>
            <div role="group" aria-label="Arrival purpose" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ARRIVAL_PURPOSES.map(p => (
                <button key={p.id} type="button" aria-pressed={form.arrival_purpose === p.id}
                  className={`pl-btn ${form.arrival_purpose === p.id ? 'pl-btn--active' : ''}`} onClick={() => setArrivalPurpose(p.id)}>{p.label}</button>
              ))}
            </div>
            {form.arrival_purpose === 'treatment' && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Players see this as optional: whoever wants medical treatment can come from this time.</span>
            )}
          </div>
        )}

        <label className="pl-label">Title<input className="pl-input" value={form.title} onChange={e => set('title', e.target.value)} /></label>

        <label className="pl-label">Day
          <select className="pl-input" value={form.date} onChange={e => set('date', e.target.value)}>
            {week.days.map(d => <option key={d.date} value={d.date}>{fmtDayShort(d.date)}{d.label.text ? ` (${d.label.text})` : ''}</option>)}
          </select>
        </label>

        <div style={field}>
          <span style={sectionTitle}>Time</span>
          <div role="group" aria-label="Timing" style={{ display: 'flex', gap: 6 }}>
            <button type="button" className={`pl-btn ${form.timing === 'clock' ? 'pl-btn--active' : ''}`} onClick={() => set('timing', 'clock')}>Clock time</button>
            <button type="button" className={`pl-btn ${form.timing === 'kickoff' ? 'pl-btn--active' : ''}`} disabled={!canAnchor && form.timing !== 'kickoff'}
              title={canAnchor ? '' : 'Only on a game day'} onClick={() => set('timing', 'kickoff')}>Anchored to kickoff</button>
          </div>
          {form.timing === 'clock' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="pl-label" style={{ flex: 1 }}>Start<input className="pl-input" type="time" step="300" value={form.start_time} onChange={e => set('start_time', e.target.value)} /></label>
              <label className="pl-label" style={{ flex: 1 }}>End<input className="pl-input" type="time" step="300" value={form.end_time} onChange={e => set('end_time', e.target.value)} /></label>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                Starts
                <input className="pl-input" type="number" min="0" step="5" style={{ width: 72 }} aria-label="Minutes from kickoff" value={koAbs} onChange={e => setKo(e.target.value, koDir)} />
                min
                <select className="pl-input" aria-label="Before or after kickoff" value={koDir} onChange={e => setKo(koAbs, e.target.value)}>
                  <option value="before">before</option>
                  <option value="after">after</option>
                </select>
                kickoff
              </div>
              <label className="pl-label">Duration (min)<input className="pl-input" type="number" min="5" step="5" value={form.duration_min} onChange={e => set('duration_min', e.target.value)} /></label>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {fixture ? (koPreview ? `Kickoff ${fixture.kickoff_time} → ${koPreview}. Follows the kickoff if it moves.` : 'Kickoff time not known yet; the block gets its time once it is.') : 'No game on this day.'}
              </span>
            </div>
          )}
        </div>

        {form.category === 'rotation' && form.rotation && (
          <div style={field}>
            <span style={sectionTitle}>Rotation</span>
            <label className="pl-label">Swap at<input className="pl-input" type="time" step="300" value={form.rotation.swap_at} onChange={e => set('rotation', { ...form.rotation, swap_at: e.target.value })} /></label>
            {[['station_a', 'Station A'], ['station_b', 'Station B']].map(([k, label]) => (
              <div key={k} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label className="pl-label" style={{ flex: '1 1 130px' }}>{label}
                  <select className="pl-input" value={form.rotation[k].category} onChange={e => setStation(k, { category: e.target.value, title: catOf(e.target.value).default_title || form.rotation[k].title })}>
                    {stationOptions.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </label>
                <label className="pl-label" style={{ flex: '1 1 130px' }}>Title<input className="pl-input" value={form.rotation[k].title} onChange={e => setStation(k, { title: e.target.value })} /></label>
                <label className="pl-label" style={{ flex: '1 1 90px' }}>Location<input className="pl-input" value={form.rotation[k].location || ''} onChange={e => setStation(k, { location: e.target.value })} /></label>
              </div>
            ))}
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Group 1: A {form.start_time}–{form.rotation.swap_at}, then B until {form.end_time}. Group 2 the other way round.
              Players only see their own group's two parts. Set the groups per day with the Groups button in the day header.
            </span>
          </div>
        )}

        {meetingCapable && (
          <label className="pl-label">Meeting slots of (minutes, empty = no slots)
            <input className="pl-input" type="number" min="5" max="60" step="5" value={form.slot_minutes} onChange={e => set('slot_minutes', e.target.value)} placeholder="e.g. 10" />
          </label>
        )}

        {hasGym && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', border: '1px dashed var(--border-strong)', borderRadius: 8, padding: 8 }}>
            {day?.gym?.has_schedule
              ? <>Gym content comes from the Week Schedule{day.gym.team_wide ? ' (team-wide focus)' : ''}: upper body {day.gym.upper} · lower body {day.gym.lower}{day.gym.other ? ` · other ${day.gym.other}` : ''} · nothing assigned {day.gym.none}. Each player sees their own, e.g. "Gym · upper body".</>
              : <>No Week Schedule for this day yet, so players just see the block title. Gym content is assigned in the Week Schedule, not here.</>}
          </div>
        )}

        <label className="pl-label">Location<input className="pl-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Pitch 1, Gym, Meeting room" /></label>

        <div style={field}>
          <span style={sectionTitle}>Who</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {AUDIENCE_CHIPS.map(c => (
              <button key={c.token} type="button" aria-pressed={form.audience.includes(c.token)}
                className={`pl-btn ${form.audience.includes(c.token) ? 'pl-btn--active' : ''}`} style={{ minHeight: 30, padding: '4px 10px' }}
                onClick={() => toggleToken(c.token)}>{c.label}</button>
            ))}
          </div>
          <details style={{ fontSize: 13 }} open={playerIds.length > 0}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>Named players{playerIds.length ? ` (${playerIds.length})` : ''}</summary>
            <input className="pl-input" style={{ marginTop: 6, width: '100%', boxSizing: 'border-box' }} placeholder="Filter players" value={playerFilter} onChange={e => setPlayerFilter(e.target.value)} />
            <div style={{ maxHeight: 190, overflowY: 'auto', marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {players.map(p => (
                <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 2px' }}>
                  <input type="checkbox" checked={playerIds.includes(p.id)} onChange={() => togglePlayer(p.id)} />
                  <span style={{ width: 26, color: 'var(--text-muted)' }}>{p.squad_number ?? ''}</span>{p.name}
                </label>
              ))}
            </div>
          </details>
        </div>

        {form.category === 'routine' && form.routine && (
          <div style={field}>
            <span style={sectionTitle}>Routine</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="pl-label" style={{ flex: 1 }}>Treatment from (optional for players)
                <input className="pl-input" type="time" step="300" value={form.routine.treatment_from || ''} onChange={e => setRoutine({ treatment_from: e.target.value || null })} />
              </label>
              <label className="pl-label" style={{ flex: 1 }}>Everybody present
                <input className="pl-input" type="time" step="300" value={form.routine.everybody_at || ''} onChange={e => setRoutine({ everybody_at: e.target.value || null })} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="pl-label" style={{ flex: 1.3 }}>Meal<input className="pl-input" value={form.routine.meal?.label || ''} onChange={e => setMeal({ label: e.target.value })} /></label>
              <label className="pl-label" style={{ flex: 1 }}>From<input className="pl-input" type="time" step="300" value={form.routine.meal?.start || ''} onChange={e => setMeal({ start: e.target.value })} /></label>
              <label className="pl-label" style={{ flex: 1 }}>To<input className="pl-input" type="time" step="300" value={form.routine.meal?.end || ''} onChange={e => setMeal({ end: e.target.value })} /></label>
            </div>
          </div>
        )}

        {form.category === 'meeting_video' && (
          <div style={field}>
            <span style={sectionTitle}>Topics</span>
            {form.topics.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="pl-input" style={{ flex: 1, minWidth: 0 }} value={t.text} aria-label={`Topic ${i + 1}`} placeholder="e.g. Review MVV – team" onChange={e => setTopic(i, { text: e.target.value })} />
                <select className="pl-input" aria-label="Topic audience" value={t.audience} onChange={e => setTopic(i, { audience: e.target.value })}>
                  <option value="players">Players</option>
                  <option value="staff">Staff only</option>
                </select>
                <button type="button" className="pl-btn" aria-label="Remove topic" onClick={() => set('topics', form.topics.filter((_, j) => j !== i))}><X size={13} /></button>
              </div>
            ))}
            <button type="button" className="pl-btn" style={{ alignSelf: 'flex-start' }} onClick={() => set('topics', [...form.topics, { text: '', audience: 'players' }])}><Plus size={13} /> Add topic</button>
          </div>
        )}

        <label className="pl-label">Specific points (players see this)
          <textarea className="pl-input" rows={3} value={form.specific_points} onChange={e => set('specific_points', e.target.value)} />
        </label>
        <label className="pl-label">Staff note (never sent to players)
          <textarea className="pl-input" rows={2} value={form.staff_note} onChange={e => set('staff_note', e.target.value)} />
        </label>
      </fieldset>

      {!isNew && block.slot_minutes && (
        <div style={{ ...field, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <span style={sectionTitle}>Meeting slots (each player only sees their own)</span>
          <SlotsEditor key={JSON.stringify(block.slots)} block={block} players={week.players} groups={day?.groups || { 1: [], 2: [] }}
            canEdit={canEdit} busy={busy} stale={slotsStale} onSave={onSaveSlots} onAutofill={onAutofillSlots} />
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="pl-btn pl-btn--primary" disabled={busy || !form.title.trim()} onClick={submit}>{isNew ? 'Add block' : 'Save'}</button>
          {!isNew && <button className="pl-btn" disabled={busy} onClick={onDelete}><Trash2 size={14} /> Delete</button>}
        </div>
      )}

      {canEdit && !isNew && (
        <div style={{ ...field, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <span style={sectionTitle}>Duplicate to other days</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {otherDays.map(d => (
              <label key={d.date} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={dupDates.includes(d.date)}
                  onChange={() => setDupDates(prev => (prev.includes(d.date) ? prev.filter(x => x !== d.date) : [...prev, d.date]))} />
                {fmtDayShort(d.date).slice(0, 6)}
              </label>
            ))}
          </div>
          <button className="pl-btn" style={{ alignSelf: 'flex-start' }} disabled={busy || !dupDates.length} onClick={() => onDuplicate(dupDates)}>
            <Copy size={14} /> Duplicate{dupDates.length ? ` to ${dupDates.length} day${dupDates.length === 1 ? '' : 's'}` : ''}
          </button>
        </div>
      )}
    </Overlay>
  );
}
